/**
 * AI CONTEXT
 * Purpose: shared optimistic-rotation queue for artwork tile surfaces.
 *   Rotate clicks update the pending map instantly — cumulative and
 *   deliberately unbounded (270° + 90° displays as 360°, not 0°) so the
 *   animation only ever turns forward — and the batched absolute targets
 *   save debounced once the clicks settle, flushing on unmount so a
 *   navigation never loses the write. The save helper applies the modulo.
 * Consumers: StudentArtworkTilesSection (`settleAfterSave` — the student doc
 *   is a live subscription whose echo replaces the pending angle) and
 *   QualityCheckTiles (keeps pendings — its snapshot is a one-shot read with
 *   no echo, so dropping a pending would snap the tile back to a stale
 *   angle; a kept pending is always the saved angle mod 360, so it can never
 *   contradict a later re-read). A crop-editor save calls `yieldToEditor`
 *   so a queued tile flush cannot land after the editor's angle, then
 *   `clearPending` once that angle is on the local crop override.
 */
import { debounce, isEmpty } from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { ARTWORK_ROTATION_STEP_DEGREES } from "#components/forms/AddStudent/studentArtworkTilesHelpers"

/**
 * One save, this long after the last rotate click. Clicks before it lands
 * only update the pending angle, so a burst of turns does not each wait on
 * a write. The profile Artwork tab and Quality Check share this queue.
 */
export const ROTATION_SAVE_DELAY_MS = 500

export const useArtworkRotationQueue = ({
  onSave,
  settleAfterSave,
}: {
  /**
   * Saves the given absolute rotation targets (degrees, by artwork id) in
   * one call. Invoked debounced and fire-and-forget; undefined disables the
   * queue (surfaces hide their rotate buttons alongside).
   */
  onSave:
    | ((rotationTargets: Record<string, number>) => Promise<void>)
    | undefined
  /**
   * Drop a pending angle once its save lands (unless a newer click replaced
   * it). Only correct when a live subscription echoes the saved value back;
   * a one-shot surface must keep pendings or the tile snaps to a stale angle.
   */
  settleAfterSave: boolean
}) => {
  // The pending map is what tiles render, ahead of the save; `pendingSaveRef`
  // is the queue the flush drains.
  const [pendingRotations, setPendingRotations] = useState<
    Record<string, number>
  >({})
  const pendingSaveRef = useRef<Record<string, number>>({})
  // Bumped when the crop editor takes over an angle, so a flush that already
  // copied the queue skips that id. `inflightRef` is a chain of every flush
  // still running: replacing it would let an earlier save finish after the
  // editor and overwrite the angle the editor just stored.
  const epochRef = useRef<Record<string, number>>({})
  const inflightRef = useRef<Promise<void>>(Promise.resolve())

  // The flush must stay referentially stable for the debounce timer to
  // survive re-renders, so it reads the latest save callback through a ref.
  const onSaveRef = useRef(onSave)
  useEffect(() => {
    onSaveRef.current = onSave
  }, [onSave])

  const flush = useCallback(() => {
    const queued = pendingSaveRef.current
    if (isEmpty(queued) || !onSaveRef.current) return
    pendingSaveRef.current = {}
    const epochsAtFlush: Record<string, number> = {}
    for (const id of Object.keys(queued)) {
      epochsAtFlush[id] = epochRef.current[id] ?? 0
    }
    const save = onSaveRef.current
    const run = (async () => {
      const targets: Record<string, number> = {}
      for (const [id, degrees] of Object.entries(queued)) {
        if ((epochRef.current[id] ?? 0) === epochsAtFlush[id]) {
          targets[id] = degrees
        }
      }
      if (isEmpty(targets)) return
      // Fire and forget: the display keeps showing the pending angle until the
      // save's echo lands (subscription surfaces), then the override is
      // dropped — but only where no newer click has replaced it.
      try {
        await save(targets)
      } finally {
        if (!settleAfterSave) return
        setPendingRotations((previous) => {
          const next = { ...previous }
          for (const [id, target] of Object.entries(targets)) {
            if (
              next[id] === target &&
              (epochRef.current[id] ?? 0) === epochsAtFlush[id]
            ) {
              delete next[id]
            }
          }
          return next
        })
      }
    })()
    const settled = run.then(
      () => undefined,
      () => undefined
    )
    const previous = inflightRef.current
    inflightRef.current = previous.then(
      () => settled,
      () => settled
    )
  }, [settleAfterSave])

  const debouncedFlush = useMemo(
    () => debounce(flush, ROTATION_SAVE_DELAY_MS),
    [flush]
  )

  // Leaving the surface (tab switch, navigation) saves whatever is queued
  // instead of dropping it.
  useEffect(() => () => debouncedFlush.flush(), [debouncedFlush])

  const rotate = useCallback(
    (artworkId: string, savedRotation: number) => {
      setPendingRotations((previous) => {
        const base = previous[artworkId] ?? savedRotation
        const next = base + ARTWORK_ROTATION_STEP_DEGREES
        pendingSaveRef.current = {
          ...pendingSaveRef.current,
          [artworkId]: next,
        }
        return { ...previous, [artworkId]: next }
      })
      debouncedFlush()
    },
    [debouncedFlush]
  )

  // The crop editor owns this piece's angle. Drop it from the queue so a
  // tile flush cannot overwrite the editor, and resolve after every flush
  // already running — including an earlier one a later flush did not replace.
  const yieldToEditor = useCallback((artworkId: string): Promise<void> => {
    epochRef.current[artworkId] = (epochRef.current[artworkId] ?? 0) + 1
    if (artworkId in pendingSaveRef.current) {
      const next = { ...pendingSaveRef.current }
      delete next[artworkId]
      pendingSaveRef.current = next
    }
    return inflightRef.current
  }, [])

  const clearPending = useCallback((artworkId: string) => {
    setPendingRotations((previous) => {
      if (!(artworkId in previous)) return previous
      const next = { ...previous }
      delete next[artworkId]
      return next
    })
  }, [])

  // Put a disarmed tile angle back on the queue when the editor save fails,
  // so the click is not lost and is not applied twice.
  const restorePending = useCallback(
    (artworkId: string, degrees: number) => {
      setPendingRotations((previous) => ({
        ...previous,
        [artworkId]: degrees,
      }))
      pendingSaveRef.current = {
        ...pendingSaveRef.current,
        [artworkId]: degrees,
      }
      debouncedFlush()
    },
    [debouncedFlush]
  )

  return {
    pendingRotations,
    rotate,
    yieldToEditor,
    clearPending,
    restorePending,
  }
}
